'use client';

import { ArrowRight, ChatBubbleLeftRightSolid, WarningSolid } from '@byway/icons';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { formatDashedYYYYMMDD, stringifySource } from 'utils';
import { Button } from '~/components/Button';
import { ListingCard } from '~/components/ListingCard';
import { SimpleMarkdown } from '~/components/Markdown';
import { TripCardCta } from '~/components/marketing/TripCard';
import { PromoBadge } from '~/components/PromoBadge';
import { PopTip } from '~/components/Tooltip';
import { useCurrencyContext } from '~/contexts/Currencies';
import { getPathname, useRouter } from '~/i18n/navigation';
import { useBlueprintIndexStore, useSortedBlueprints } from '~/stores/blueprintIndexStore';
import { api } from '~/trpc/react-client';
import { trackBlueprintClicked, trackEvent } from '~/utils';
import { buildJourneyRequestBody, type CreateJourneyBody, getCacheKey } from '~/utils/blueprintHelpers/buildJourneyRequestBody';
import { getCookie } from '~/utils/cookies';
import { ensureFutureDateString } from '~/utils/dateHelpers/dateHelpers';
import { getLocalisedTierName } from '~/utils/loyaltyBenefitsCopy';
import { preloadQueue } from '~/utils/PreloadQueue';
import { CURRENT_PROMO, isBlueprintPromoEligible } from '~/utils/promoHelper';
import { clearTripRepriceStarted, markTripRepriceStarted, tripRepriceKey } from '~/utils/tpv2/tripReprice';
import { getLocalePrefixTrackingParams } from '~/utils/tracking';
import { isTripV2Enabled } from '~/utils/tripV2Flag';
import type { PricedBlueprint } from '../BlueprintIndexSearch/BlueprintIndexSearch';
import styles from './BlueprintCards.module.css';
const DEFAULT_PAX_COUNT = 2;
type BlueprintCardsProps = {
  loyaltyDiscount?: number;
  loyaltyTierId?: string;
  loyaltyTierName?: string;
};
export function BlueprintCards({
  loyaltyDiscount = 0,
  loyaltyTierId,
  loyaltyTierName
}: BlueprintCardsProps = {}) {
  const t = useTranslations();
  const locale = useLocale();
  const [numResults, setNumResults] = React.useState(12);
  const abortControllerRef = useRef<AbortController | null>(null);
  const blueprints = useSortedBlueprints();
  const {
    ref,
    inView
  } = useInView({
    threshold: 0
  });
  const router = useRouter();
  const path = usePathname();
  const queryClient = useQueryClient();
  const storeStartDate = useBlueprintIndexStore(state => state.startDate);
  const loading = useBlueprintIndexStore(state_0 => state_0.loading);
  const displayCountry = getCookie('displayCountry');
  const isUsCustomer = displayCountry === 'US';
  const isUkCustomer = displayCountry === 'GB';
  const {
    isPromoLive
  } = CURRENT_PROMO();
  const showPromoBadges = isUkCustomer && isPromoLive;
  const blueprintMap = useMemo(() => new Map(blueprints.map(bp => [bp.blueprintId, bp])), [blueprints]);
  const visibleBlueprints = useMemo(() => blueprints.slice(0, numResults), [blueprints, numResults]);
  const isScrollingRef = useRef(false);
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      isScrollingRef.current = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);
  const getScrollPosition = useCallback(() => {
    if (typeof window === 'undefined') return 0;
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const numResultsKey = `bpi_cards_numResults:${path || ''}`;
    const scrollKey = `bpi_cards_scroll:${path || ''}`;
    const storedNumResults = window.sessionStorage.getItem(numResultsKey);
    const storedScrollPosition = window.sessionStorage.getItem(scrollKey);
    if (storedNumResults) {
      const parsedNumResults = Number.parseInt(storedNumResults, 10);
      if (!Number.isNaN(parsedNumResults) && parsedNumResults > 0) {
        setNumResults(parsedNumResults);
      }
    }
    if (!storedScrollPosition) {
      if (storedNumResults) {
        window.sessionStorage.removeItem(numResultsKey);
      }
      return;
    }
    const savedScrollPosition = Number.parseInt(storedScrollPosition, 10);
    if (Number.isNaN(savedScrollPosition)) {
      window.sessionStorage.removeItem(scrollKey);
      if (storedNumResults) {
        window.sessionStorage.removeItem(numResultsKey);
      }
      return;
    }
    setTimeout(() => {
      const currentScrollPosition = getScrollPosition();
      if (currentScrollPosition < 100) {
        window.scrollTo({
          top: savedScrollPosition,
          behavior: 'auto'
        });
      }
      window.sessionStorage.removeItem(scrollKey);
      window.sessionStorage.removeItem(numResultsKey);
    }, 100);
  }, [path, getScrollPosition]);
  useEffect(() => {
    if (inView) {
      startTransition(() => {
        setNumResults(prev => prev + 12);
      });
    }
  }, [inView]);
  const blueprintIndexRequestSource = useMemo(() => stringifySource({
    environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
    name: 'blueprintIndex',
    action: 'create'
  }), []);
  const getProcessBlueprintCacheKey = useCallback((input: {
    journeyId: string;
    startDate: string | null;
    returnJourneyData: boolean;
  }) => ['processBlueprint', input.journeyId, input.startDate, input.returnJourneyData], []);
  const createJourneyMutation = api.blueprint.processBlueprint.useMutation({
    onSuccess: (data, variables) => {
      const cacheKey = getProcessBlueprintCacheKey({
        journeyId: variables.journeyId,
        startDate: variables.startDate,
        returnJourneyData: variables.returnJourneyData ?? false
      });
      queryClient.setQueryData(cacheKey, data);
    }
  });
  const handleCardClick = useCallback(async (blueprintId: string) => {
    const foundBlueprint = blueprintMap.get(blueprintId);
    if (!foundBlueprint) return;
    const rawStartDate = foundBlueprint.startDate ?? storeStartDate ?? null;
    const effectiveStartDate = ensureFutureDateString(rawStartDate);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const cacheKey_0 = getCacheKey(foundBlueprint.blueprintId, effectiveStartDate);
    const startTripReprice = (tripId: string, body: CreateJourneyBody) => {
      const {
        startDate
      } = body;
      const input_0 = {
        journeyId: tripId,
        childAges: [],
        numberOfAdults: DEFAULT_PAX_COUNT,
        numberOfRooms: 1,
        originLocationId: body.originLocationId,
        startDate,
        twinRoomPreferred: false,
        requestSource: blueprintIndexRequestSource,
        useYearRoundPricingCache: false,
        updateSelectedJourney: true,
        countryCodeOfResidence: undefined,
        returnJourneyData: true,
        journeyTitleOverride: body.buildParameters.journeyTitleOverride
      };
      if (!isTripV2Enabled()) {
        const alreadyRepriced = queryClient.getQueryData(getProcessBlueprintCacheKey({
          journeyId: tripId,
          startDate,
          returnJourneyData: true
        }));
        if (!alreadyRepriced) createJourneyMutation.mutate(input_0);
        return;
      }
      markTripRepriceStarted(tripId);
      void queryClient.fetchQuery({
        queryKey: tripRepriceKey(tripId),
        queryFn: () => createJourneyMutation.mutateAsync(input_0),
        staleTime: Infinity,
        retry: 0
      }).then(() => clearTripRepriceStarted(tripId)).catch(() => undefined);
    };
    try {
      if (typeof window !== 'undefined') {
        const scrollKey_0 = `bpi_cards_scroll:${path || ''}`;
        const currentScrollPosition_0 = getScrollPosition();
        window.sessionStorage.setItem(scrollKey_0, String(currentScrollPosition_0));
        const cardIndex = blueprints.findIndex(blueprintItem => blueprintItem.blueprintId === foundBlueprint.blueprintId);
        const minNumResults = cardIndex >= 0 ? Math.max(numResults, cardIndex + 1) : numResults;
        const numResultsKey_0 = `bpi_cards_numResults:${path || ''}`;
        window.sessionStorage.setItem(numResultsKey_0, String(minNumResults));
      }
      const cached = queryClient.getQueryData<{
        uniqueId: string;
      }>(cacheKey_0);
      if (cached) {
        const body_0 = buildJourneyRequestBody(foundBlueprint, effectiveStartDate, locale);
        if (body_0) {
          startTripReprice(cached.uniqueId, body_0);
        }
        const url = `/journey/${cached.uniqueId}?source=blueprintIndex`;
        router.push(url, {
          scroll: true
        });
        trackEvent('bpi_card_see_trip', {
          event_category: 'Blueprint_Index',
          event_label: 'bpi_card_see_trip',
          ...getLocalePrefixTrackingParams(getPathname({
            href: url,
            locale
          }), locale)
        });
        return;
      }
      const body_1 = buildJourneyRequestBody(foundBlueprint, effectiveStartDate, locale);
      if (!body_1) {
        throw new Error('Missing start date');
      }
      const firstCallCacheKey = getProcessBlueprintCacheKey({
        journeyId: body_1.journeyId,
        startDate: body_1.startDate,
        returnJourneyData: false
      });
      let data_0 = queryClient.getQueryData<{
        uniqueId: string;
      }>(firstCallCacheKey);
      if (!data_0) {
        data_0 = await createJourneyMutation.mutateAsync({
          journeyId: body_1.journeyId,
          childAges: body_1.childAges ?? [],
          numberOfAdults: body_1.adult_count,
          numberOfRooms: body_1.room_count,
          originLocationId: body_1.originLocationId,
          startDate: body_1.startDate,
          twinRoomPreferred: body_1.twinRoomPreferred,
          requestSource: body_1.requestSource,
          useYearRoundPricingCache: body_1.useYearRoundPricingCache,
          updateSelectedJourney: body_1.updateSelectedJourney ?? false,
          countryCodeOfResidence: undefined,
          returnJourneyData: false,
          journeyTitleOverride: body_1.buildParameters.journeyTitleOverride
        });
      }
      if (abortController.signal.aborted) {
        return;
      }
      const createdJourneyId = data_0?.uniqueId;
      if (!createdJourneyId) {
        throw new Error('Missing journey id after blueprint is processed');
      }
      if (!abortController.signal.aborted) {
        startTripReprice(createdJourneyId, body_1);
      }
      queryClient.setQueryData(cacheKey_0, {
        uniqueId: createdJourneyId
      });
      const url_0 = `/journey/${createdJourneyId}?source=blueprintIndex`;
      router.push(url_0, {
        scroll: true
      });
      trackEvent('bpi_card_see_trip', {
        event_category: 'Blueprint_Index',
        event_label: 'bpi_card_see_trip',
        ...getLocalePrefixTrackingParams(getPathname({
          href: url_0,
          locale
        }), locale)
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      throw error;
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [blueprintMap, storeStartDate, queryClient, router, locale, createJourneyMutation, path, numResults, blueprints, getScrollPosition, blueprintIndexRequestSource, getProcessBlueprintCacheKey]);
  return <div className={styles.cardsContainer}>
      <div className={styles.cards} aria-live="polite">
        {visibleBlueprints.map(blueprint => {
        const rawStartDate_0 = blueprint.startDate ?? storeStartDate ?? null;
        const effectiveStartDate_0 = ensureFutureDateString(rawStartDate_0);
        const formattedStartDate = formatDashedYYYYMMDD(effectiveStartDate_0);
        return <BlueprintCard key={blueprint.blueprintId} blueprint={blueprint} onCardClick={() => handleCardClick(blueprint.blueprintId)} formattedStartDate={formattedStartDate} isUsCustomer={isUsCustomer} showPromoBadge={showPromoBadges && isBlueprintPromoEligible(blueprint.eligiblePromotions)} isScrollingRef={isScrollingRef} loyaltyDiscount={loyaltyDiscount} loyaltyTierId={loyaltyTierId} loyaltyTierName={loyaltyTierName} />;
      })}
      </div>

      {!loading && blueprints.length === 0 && <section className={styles.noTrips} aria-label={t('content.blueprintIndex.no_trips_aria')} aria-live="polite">
          {/* Bare <a>: /concierge is a localised slug, so a locale-prefixing Link would 404 on any
              locale that translates it. TODO: use the shared href resolver once G2-3863 lands. */}
          <a href="/concierge" className={styles.noTripsBlock} aria-label={t('content.blueprintIndex.concierge_aria')}>
            <div className={styles.noTripsBlockContent}>
              <div className={styles.noTripsIconCircle} aria-hidden="true">
                <ChatBubbleLeftRightSolid size="scale" decorative />
              </div>
              <div className={styles.noTripsBlockText}>
                <span className={styles.noTripsBlockDescription}>{t('content.feedback.search_trips_empty')}</span>
              </div>
              <ArrowRight size="large" className={styles.noTripsArrow} decorative color="var(--by-color-icon-default)" />
            </div>
          </a>
        </section>}
      {numResults < blueprints.length && <div ref={ref}>
          <div className="h-4"></div>
          <Button onPress={() => {
        setNumResults(prev_0 => prev_0 + 12);
      }} variant="tertiary">
            {t('common.see_more_results')}
          </Button>
        </div>}
    </div>;
}
type BlueprintCardProps = {
  blueprint: PricedBlueprint;
  onCardClick: () => Promise<void>;
  formattedStartDate: string | null;
  isUsCustomer: boolean;
  showPromoBadge: boolean;
  isScrollingRef: React.RefObject<boolean>;
  loyaltyDiscount?: number;
  loyaltyTierId?: string;
  loyaltyTierName?: string;
};
function BlueprintCard({
  blueprint,
  onCardClick,
  formattedStartDate,
  isUsCustomer,
  showPromoBadge,
  isScrollingRef,
  loyaltyDiscount = 0,
  loyaltyTierId,
  loyaltyTierName
}: Readonly<BlueprintCardProps>) {
  const t = useTranslations();
  const tAccount = useTranslations('account');
  const locale = useLocale();
  const [error, setError] = useState<string | null>(null);
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  const queryClient = useQueryClient();
  const cacheKey = useMemo(() => formattedStartDate ? ['journeyFromBlueprint', blueprint.blueprintId, formattedStartDate] : ['journeyFromBlueprint', blueprint.blueprintId, 'no-start'], [blueprint.blueprintId, formattedStartDate]);
  const getProcessBlueprintCacheKey = useCallback((input: {
    journeyId: string;
    startDate: string | null;
    returnJourneyData: boolean;
  }) => ['processBlueprint', input.journeyId, input.startDate, input.returnJourneyData], []);
  const createJourneyMutation = api.blueprint.processBlueprint.useMutation({
    onSuccess: (data, variables) => {
      const cacheKey_0 = getProcessBlueprintCacheKey({
        journeyId: variables.journeyId,
        startDate: variables.startDate,
        returnJourneyData: variables.returnJourneyData ?? false
      });
      queryClient.setQueryData(cacheKey_0, data);
    }
  });
  const imageData = blueprint.image.responsiveImage;
  const {
    ref: intersectionRef,
    inView
  } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
    triggerOnce: false
  });
  const price = useMemo(() => isUsCustomer ? blueprint.totalPrepayAmountUS : blueprint.price, [isUsCustomer, blueprint.totalPrepayAmountUS, blueprint.price]);
  const adjustedPrice = useMemo(() => convertPriceInGBP(Math.round(price / DEFAULT_PAX_COUNT)), [price, convertPriceInGBP]);
  const hasLoyaltyDiscount = loyaltyDiscount > 0 && Boolean(loyaltyTierName);
  const discountedPrice = hasLoyaltyDiscount ? convertPriceInGBP(Math.round(price / DEFAULT_PAX_COUNT * (1 - loyaltyDiscount / 100))) : null;
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const preloadJourney = useCallback(async () => {
    if (!formattedStartDate) return;
    if (queryClient.getQueryData(cacheKey)) return;
    const cacheKeyString = JSON.stringify(cacheKey);
    await preloadQueue.add(cacheKeyString, async signal => {
      const body = buildJourneyRequestBody(blueprint, formattedStartDate, locale);
      if (!body) return;
      if (signal.aborted) return;
      const firstCallCacheKey = getProcessBlueprintCacheKey({
        journeyId: body.journeyId,
        startDate: body.startDate,
        returnJourneyData: false
      });
      let data_0 = queryClient.getQueryData<{
        uniqueId: string;
      }>(firstCallCacheKey);
      if (!data_0) {
        data_0 = await createJourneyMutation.mutateAsync({
          journeyId: body.journeyId,
          childAges: body.childAges ?? [],
          numberOfAdults: body.adult_count,
          numberOfRooms: body.room_count,
          originLocationId: body.originLocationId,
          startDate: body.startDate,
          twinRoomPreferred: body.twinRoomPreferred,
          requestSource: body.requestSource,
          useYearRoundPricingCache: body.useYearRoundPricingCache,
          updateSelectedJourney: body.updateSelectedJourney ?? false,
          countryCodeOfResidence: undefined,
          returnJourneyData: false
        });
      }
      if (signal.aborted) return;
      const createdJourneyId = data_0?.uniqueId;
      if (!createdJourneyId) return;
      queryClient.setQueryData(cacheKey, {
        uniqueId: createdJourneyId
      });
    });
  }, [formattedStartDate, queryClient, cacheKey, blueprint, createJourneyMutation, getProcessBlueprintCacheKey, locale]);
  useEffect(() => {
    if (!inView || !formattedStartDate) return;
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }
    preloadTimeoutRef.current = setTimeout(() => {
      if (!isScrollingRef.current) {
        preloadJourney();
      }
    }, 500);
    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
    };
  }, [inView, formattedStartDate, preloadJourney]);
  const handleCardClick = useCallback(async () => {
    setError(null);
    trackBlueprintClicked({
      blueprintId: blueprint.blueprintId,
      title: blueprint.title,
      tags: blueprint.tags
    });
    try {
      await onCardClick();
    } catch (error_0: any) {
      if (error_0?.name !== 'AbortError') {
        setError(t('content.feedback.trip_error_get'));
      }
    }
  }, [onCardClick, blueprint]);
  useEffect(() => {
    setError(null);
  }, [formattedStartDate]);
  return <article ref={intersectionRef} data-test-id="trip-card">
      <ListingCard hasBackground variant="trip" fullHeight role="link" tabIndex={0} onClick={(event: React.MouseEvent<HTMLDivElement>) => {
      const targetElement = event.target as HTMLElement | null;
      if (targetElement?.closest('[data-blueprint-card-cta="true"]')) {
        return;
      }
      handleCardClick();
    }} onKeyDown={(event_0: React.KeyboardEvent<HTMLDivElement>) => {
      if (event_0.key === 'Enter' || event_0.key === ' ') {
        event_0.preventDefault();
        handleCardClick();
      }
    }} aria-labelledby={`card-title-${blueprint.blueprintId}`} aria-describedby={`card-desc-${blueprint.blueprintId}`}>
        <div className={styles.imageButtonSlot}>
          <ListingCard.Gallery images={[{
          data: imageData
        }]} tag={showPromoBadge ? <PromoBadge /> : null} />
        </div>

        <ListingCard.Content>
          <div className={styles.details}>
            <div className={styles.contentArea}>
              <ListingCard.Title as="h2" id={`card-title-${blueprint.blueprintId}`}>
                {blueprint.title}
              </ListingCard.Title>
              {(blueprint.shortDescription || blueprint.description) && <div id={`card-desc-${blueprint.blueprintId}`} className={styles.descriptionText}>
                  <SimpleMarkdown>{blueprint.shortDescription || blueprint.description}</SimpleMarkdown>
                </div>}
            </div>

            {hasLoyaltyDiscount && <div className={styles.loyaltyBadges}>
                <span className={styles.tierBadge}>
                  {getLocalisedTierName(tAccount, loyaltyTierId, loyaltyTierName ?? '')}
                </span>
                <span className={styles.discountBadge}>-{loyaltyDiscount}%</span>
              </div>}
            <div className={styles.cardBottomRow}>
              <div className={styles.priceNights}>
                <PopTip content={t('content.price_disclaimer')}>
                  <span className={styles.priceTrigger}>
                    <span className={styles.priceFrom}>{t('common.from')}</span>{' '}
                    <span className={`${styles.cardPrice} ${hasLoyaltyDiscount ? styles.cardPriceDiscounted : ''}`}>
                      {t('common.price_pp', {
                      price: discountedPrice ?? adjustedPrice
                    })}
                    </span>
                    {hasLoyaltyDiscount && <span className={styles.originalPrice}>{adjustedPrice}</span>}
                  </span>
                </PopTip>
                <span className={styles.nightsText}>
                  / {t('content.forms.nights_count', {
                  count: blueprint.duration
                })}
                </span>
              </div>
              <TripCardCta />
            </div>
            <div className={styles.error} aria-live="polite" aria-atomic="true">
              {error && <>
                  <WarningSolid size="small" color="--by-color-icon-error" decorative />
                  <p>{error}</p>
                </>}
            </div>
          </div>
        </ListingCard.Content>
      </ListingCard>
    </article>;
}
export { BlueprintCard };